import { Fragment, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, CardContent } from "@/components/ui/card"
import { getKnowledgeItems } from "@/lib/api/knowledge"
import type { FuriganaMode } from "@/hooks/use-furigana-preference"
import { processStoryHtml } from "@/lib/furigana-processor"

interface StoryReaderProps {
  title: string
  subtitle: string
  /** Textbook + lesson label, e.g. "Minna no Nihongo I · Lesson 5". Null/absent
   *  hides the line (e.g. the admin viewer, which has no level data). */
  levelLabel?: string | null
  /** Public URL of the generated MP3 narration. Null/undefined until it has
   *  been synthesized — the audio player is hidden until then. */
  audio?: string | null
  html: string
  /** English translation, paragraph-aligned with the body (split on blank
   *  lines). Null/empty when the story has no translation. */
  translation: string | null
  mode: FuriganaMode
  firstOnly: boolean
  showTranslation: boolean
}

// Presentational reader shared by the normal story route and the admin viewer:
// subtitle + title + audio + body, with furigana applied per the current
// preference. The furigana/translation preference state is owned by the route
// (the quiz reads it too, and the settings cog lives in the route's top bar).
export function StoryReader({
  title,
  subtitle,
  levelLabel,
  audio,
  html,
  translation,
  mode,
  firstOnly,
  showTranslation,
}: StoryReaderProps) {
  const { data: knowledgeItems } = useQuery({
    queryKey: ["knowledge", "kanji"],
    queryFn: () => getKnowledgeItems({ type: "kanji", search: "" }),
    enabled: mode === "unknown",
  })

  const processedHtml = useMemo(() => {
    const knownKanji = (knowledgeItems ?? []).map((item) => item.text)
    return processStoryHtml(html, mode, firstOnly, knownKanji)
  }, [html, mode, firstOnly, knowledgeItems])

  // Split the processed body into individual paragraphs so each can be paired
  // with its translation. toHtml emits one <p> per source paragraph, so these
  // line up index-for-index with the translation paragraphs.
  const bodyParagraphs = useMemo(() => {
    const doc = new DOMParser().parseFromString(
      `<div>${processedHtml}</div>`,
      "text/html"
    )
    return Array.from(doc.querySelectorAll("p")).map((p) => p.innerHTML)
  }, [processedHtml])

  // Translation split into paragraphs (blank lines) and, within each, lines
  // (single newlines, e.g. dialogue turns). Empty when there's no translation.
  const translationParagraphs = useMemo(() => {
    if (!translation) return []
    return translation
      .trim()
      .split("\n\n")
      .map((paragraph) => paragraph.split("\n"))
  }, [translation])

  // Run the title through the same furigana processing as the body so its
  // furigana respects the current mode/firstOnly and the hover-to-reveal rule.
  const processedTitle = useMemo(() => {
    const knownKanji = (knowledgeItems ?? []).map((item) => item.text)
    return processStoryHtml(title, mode, firstOnly, knownKanji)
  }, [title, mode, firstOnly, knowledgeItems])

  return (
    <>
      {subtitle && (
        <p className="mb-1 text-sm tracking-widest text-muted-foreground uppercase">
          {subtitle}
        </p>
      )}
      <h1
        className="font-heading mb-0 text-[2.7rem] font-semibold tracking-tight text-foreground [&_rt]:text-[clamp(9px,2.20vw,15px)] [&_rt]:font-normal"
        dangerouslySetInnerHTML={{ __html: processedTitle }}
      />
      {levelLabel && (
        <p className="text-[9px] tracking-widest text-muted-foreground uppercase">
          {levelLabel}
        </p>
      )}
      {/* The native player: `color-scheme` themes the controls light/dark,
          `text-foreground` + `accent-foreground` tint the icons/scrubber to the
          body text color (softer than default black), and the bar surface is
          recolored via `.story-audio` pseudo-element rules in index.css. */}
      {audio && (
        <div className="mt-6 flex items-center gap-2">
          <audio
            controls
            preload="none"
            src={audio}
            className="story-audio h-8 min-w-0 flex-1 text-foreground accent-foreground [color-scheme:light] sm:max-w-sm sm:flex-none dark:[color-scheme:dark]"
          />
        </div>
      )}
      <Card className="mt-4">
        <CardContent>
          <div
            className="prose prose-sm reading-prose"
            style={{
              fontFamily: "var(--font-reading)",
              fontSize: "clamp(18px, 4.5vw, 30px)",
              lineHeight: "2em",
            }}
          >
            {bodyParagraphs.map((paragraph, i) => (
              <Fragment key={i}>
                <p dangerouslySetInnerHTML={{ __html: paragraph }} />
                {showTranslation && translationParagraphs[i] && (
                  <p
                    style={{
                      fontFamily: "system-ui, sans-serif",
                      fontSize: "clamp(15px, 3.2vw, 19px)",
                      lineHeight: "1.6",
                      color:
                        "color-mix(in srgb, var(--muted-foreground) 65%, transparent)",
                    }}
                  >
                    {translationParagraphs[i].map((line, j) => (
                      <Fragment key={j}>
                        {j > 0 && <br />}
                        {line}
                      </Fragment>
                    ))}
                  </p>
                )}
              </Fragment>
            ))}
          </div>
        </CardContent>
      </Card>
    </>
  )
}
